'use client';
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Distributor, getEBTierLabel, getRankDotColor } from '@/data/mockData';
import RankBadge from '@/components/ui/RankBadge';

interface Props {
  distributors: Distributor[];
}

type SortKey = 'name' | 'psv' | 'gsv' | 'commissionThisMonth' | 'g1BreakawayCount';

export default function TopPerformersTable({ distributors }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('gsv');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = [...distributors]
    .sort((a, b) => {
      const av = a[sortKey] as number | string;
      const bv = b[sortKey] as number | string;
      if (typeof av === 'string' && typeof bv === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    })
    .slice(0, 10);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const maxGSV = Math.max(...distributors.map(d => d.gsv));

  const colHeaders: { key: SortKey; label: string }[] = [
    { key: 'name', label: 'Distributor' },
    { key: 'psv', label: 'PSV' },
    { key: 'gsv', label: 'GSV' },
    { key: 'commissionThisMonth', label: 'Commission' },
    { key: 'g1BreakawayCount', label: 'G1 Execs' },
  ];

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-300">Top 10 Performers</h3>
        <span className="text-xs text-zinc-600">Sorted by {sortKey} — Apr 2026</span>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-zinc-800">
            <th className="text-left text-[9px] font-semibold uppercase tracking-wider text-zinc-600 px-4 py-2.5 w-8">#</th>
            {colHeaders.map(col => (
              <th
                key={`tpth-${col.key}`}
                onClick={() => handleSort(col.key)}
                className="text-left text-[9px] font-semibold uppercase tracking-wider text-zinc-600 px-4 py-2.5 cursor-pointer hover:text-zinc-400 transition-colors"
              >
                <div className="flex items-center gap-1">
                  {col.label}
                  {sortKey === col.key && (sortDir === 'desc' ? <ChevronDown size={10} className="text-violet-400" /> : <ChevronUp size={10} className="text-violet-400" />)}
                </div>
              </th>
            ))}
            <th className="text-left text-[9px] font-semibold uppercase tracking-wider text-zinc-600 px-4 py-2.5">Rank</th>
            <th className="text-left text-[9px] font-semibold uppercase tracking-wider text-zinc-600 px-4 py-2.5">EB Tier</th>
            <th className="text-left text-[9px] font-semibold uppercase tracking-wider text-zinc-600 px-4 py-2.5">GSV Share</th>
            <th className="text-left text-[9px] font-semibold uppercase tracking-wider text-zinc-600 px-4 py-2.5">Status</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((dist, i) => {
            const gsvPct = (dist.gsv / maxGSV) * 100;
            return (
              <tr key={`tp-${dist.id}`} className="border-b border-zinc-800/40 hover:bg-zinc-800/20 transition-colors">
                <td className="px-4 py-2.5">
                  <span className={`text-xs font-bold font-mono ${i === 0 ? 'text-amber-400' : i === 1 ? 'text-zinc-400' : i === 2 ? 'text-orange-700' : 'text-zinc-600'}`}>
                    {i + 1}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getRankDotColor(dist.rank)}`} />
                    <div>
                      <p className="text-sm font-semibold text-zinc-200 whitespace-nowrap">{dist.name}</p>
                      <p className="text-[10px] text-zinc-600 font-mono">{dist.id}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <span className={`text-sm font-mono font-semibold ${dist.psv >= 100 ? 'text-zinc-300' : 'text-red-400'}`}>{dist.psv}</span>
                </td>
                <td className="px-4 py-2.5">
                  <span className="text-sm font-mono font-semibold text-zinc-200">{(dist.gsv / 1000).toFixed(1)}K</span>
                </td>
                <td className="px-4 py-2.5">
                  <span className="text-sm font-mono font-semibold text-emerald-400">${dist.commissionThisMonth.toLocaleString()}</span>
                </td>
                <td className="px-4 py-2.5">
                  <span className="text-sm font-mono text-violet-400">{dist.g1BreakawayCount}</span>
                </td>
                <td className="px-4 py-2.5"><RankBadge rank={dist.rank} size="xs" /></td>
                <td className="px-4 py-2.5">
                  <span className="text-sm font-bold text-amber-400">{getEBTierLabel(dist.gsv)}</span>
                </td>
                <td className="px-4 py-2.5 w-36">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-zinc-800 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-violet-500 transition-all" style={{ width: `${gsvPct}%` }} />
                    </div>
                    <span className="text-[10px] text-zinc-600 font-mono w-8">{gsvPct.toFixed(0)}%</span>
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs font-semibold ${dist.active ? 'text-emerald-400' : 'text-red-400'}`}>
                    {dist.active ? '● Active' : '○ Inactive'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}